import { Container } from 'aurelia-dependency-injection';
import { Router } from 'aurelia-router';
import { filterStatefulQueryParameters } from '~/components/navigation/navigation-utils';
import { CommonURLParam } from '~/constants';

/**
 * Returns a URI for the route with a given name, using the current query parameters (excluding specified parameters
 * but including a specified value for 'division' and 'transaction-ref' params).
 * @param {Router} router the router with the current query parameters.
 * @param {string} name the given name of the route.
 * @param {string[]} paramsToDelete the names of the specified parameters to exclude
 * @param {string} division the specified value of the 'division' parameter.
 * @param {string} transRef the specified value of the 'transaction-ref' parameter.
 * @returns {string} the URI
 */
export function generateTransactionRefLink(
	router: Router,
	name: string,
	division: string,
	transRef: string,
	paramsToDelete?: string[],
): string | undefined {
	if (!router) {
		return undefined;
	}

	const params = filterStatefulQueryParameters(
		router.currentInstruction.queryParams,
		paramsToDelete,
	);

	params[CommonURLParam.DIVISION] = division;
	if (transRef) {
		params[CommonURLParam.TRANS_REF] = transRef;
	}

	return router.generate(name, params);
}

/**
 * Returns a URI for the route with a given name, using the current query parameters
 * with the option to include additional extra parameters or exclude other parameters.
 * @param {Router} router the router with the current query parameters.
 * @param {string} name the given name of the route.
 * @param { [name: string]: string } extraParams parameters.
 * @param {string[]} paramsToDelete the names of the specified parameters to exclude
 * @returns {string} the URI
 */
export function generateLink(
	router: Router,
	name: string,
	extraParams: { [name: string]: string },
	paramsToDelete?: string[],
): string | undefined {
	if (!router) {
		return undefined;
	}

	const params = filterStatefulQueryParameters(
		router.currentInstruction.queryParams,
		paramsToDelete,
	);
	const allParams = Object.assign({}, params, extraParams);

	return router.generate(name, allParams);
}

/**
 * Returns a URI for the route with a given name, using the current query parameters,
 * including any optional extra parameters and excluding the `customisation` parameter.
 * @param {string} name the given name of the route.
 * @param { [name: string]: string } extraParams parameters.
 * @returns {string} the URI
 */
export function generateLinkNoCustomisationParam(
	name: string,
	extraParams?: { [name: string]: string },
): string | undefined {
	const router: Router = Container.instance.get(Router);
	return generateLink(router, name, extraParams, [
		CommonURLParam.CUSTOMISATION,
	]);
}

/**
 * Returns a URI for the route with a given name, using the current query parameters (excluding specified parameters
 * but including a specified value for 'division', 'transaction-id' and 'batch-id' params).
 * @param {Router} router the router with the current query parameters.
 * @param {string} name the given name of the route.
 * @param {string} division the specified value of the 'division' parameter.
 * @param {string} transactionId the specified value of the 'transaction-id' parameter.
 * @param {string} batchId the specified value of the 'batch-id' parameter.
 * @returns {string} the URI
 */
export function generateTransactionIdLink(
	router: Router,
	name: string,
	division: string,
	transactionId: string,
	batchId: string,
): string | undefined {
	if (!router) {
		return undefined;
	}

	const params = filterStatefulQueryParameters(
		router.currentInstruction.queryParams,
	);

	params[CommonURLParam.DIVISION] = division;
	if (transactionId) {
		params[CommonURLParam.TRANSACTION_ID] = transactionId;
	}

	if (batchId) {
		params[CommonURLParam.BATCH_ID] = batchId;
	}

	return router.generate(name, params);
}
